// THE HOLE FALLBACK (rearch-r8k, ruling (B) 2026-10-31): the ONE text both
// ends stand on where the GM03 L0 node has a hole (a bore opening: no
// triangle, vrm_core skinY null) — the public terrain/L0 tile there, the
// uncarved L0 the node was built from. game/ground.js (sync file reads) and
// client/src/ground.js (a lazy fetch) inject only `tile(tx, ty)`; height /
// heightRender / stand are this file's, so a hole sample is bit-equal at both
// ends by construction (check-server-ground). A tile is read ONLY when a
// sample lands in a hole: no prefetch, no other reader (check-server-ground
// "non-hole samples fetch no terrain/L0"). Retired by the GM03 hole caps
// (work item rearch-r8k-hole-caps: an engine step bakes the cap into the node).
//
//   tile(tx, ty) -> Uint16Array(257²) | undefined (not resident / no tile)
//   stand(x, z)  -> max(height, heightRender), or null (the old
//                   ClientHeightfield.standHeight past its skin)
'use strict';
const { triSplit } = require('./lodmath');
const { platformsIndex, platformGround } = require('./pave');

class HoleFallback {
  // meta: the bake's meta.json (levels.L0, grid); stride: the drawn L0 mesh's
  // post stride (renderconfig rings[0].patch_verts, renderStride below)
  constructor(meta, tile, stride) {
    const g = meta.grid;
    this.level = meta.levels.L0;
    this.cell = this.level.cell_m;
    this.tileCells = g.tile_cells;
    this.zBase = g.z_base_m;
    this.zScale = g.z_scale_cm / 100;
    this.stride = stride;
    this.tile = tile;
    this.platforms = null;
    this._node = (i, j) => {
      const tc = this.tileCells, tx = Math.floor(Math.max(0, i) / tc), ty = Math.floor(Math.max(0, j) / tc);
      const t = this._tile(tx, ty);
      return t ? t[(Math.max(0, j) - ty * tc) * 257 + (Math.max(0, i) - tx * tc)] * this.zScale + this.zBase : NaN;
    };
  }

  // the bake's platforms (ways.json `platforms`): the bed / plane over the posts
  setPlatforms(block) {
    this.platforms = platformsIndex(block || null);
  }

  _tile(tx, ty) {
    const L = this.level;
    return tx >= 0 && ty >= 0 && tx < L.nx && ty < L.ny ? this.tile(tx, ty) : undefined;
  }

  height(x, z) {
    if (this.platforms) {
      const p = platformGround(this.platforms, x, z, this.cell, this._node);
      if (p !== null) return p;
    }
    const fx = x / this.cell - 0.5;
    const fz = z / this.cell - 0.5;
    const gx = Math.max(0, fx), gz = Math.max(0, fz);
    const tx = Math.floor(gx / this.tileCells), ty = Math.floor(gz / this.tileCells);
    const t = this._tile(tx, ty);
    if (!t) return null;
    const lx = gx - tx * this.tileCells, lz = gz - ty * this.tileCells;
    const x0 = Math.min(255, Math.floor(lx)), z0 = Math.min(255, Math.floor(lz));
    const dx = lx - x0, dz = lz - z0;
    const N = 257;
    const i = z0 * N + x0;
    const v = t[i] * (1 - dx) * (1 - dz) + t[i + 1] * dx * (1 - dz)
      + t[i + N] * (1 - dx) * dz + t[i + N + 1] * dx * dz;
    return v * this.zScale + this.zBase;
  }

  // the drawn stride-s lattice's triangle (game/lodmath.js triSplit, the
  // render mesh's own split): stride-s verts sit at (s*k + 0.5)*cell0
  heightRender(x, z) {
    const stride = this.stride;
    const cell = this.cell * stride;
    const fx = x / cell - 0.5 / stride;
    const fz = z / cell - 0.5 / stride;
    const gx = Math.max(0, fx) * stride, gz = Math.max(0, fz) * stride;
    const tx = Math.floor(gx / this.tileCells), ty = Math.floor(gz / this.tileCells);
    const t = this._tile(tx, ty);
    if (!t) return null;
    const lx = (gx - tx * this.tileCells) / stride, lz = (gz - ty * this.tileCells) / stride;
    const N = 257;
    const maxCol = this.tileCells / stride - 1;
    const x0 = Math.min(maxCol, Math.floor(lx)), z0 = Math.min(maxCol, Math.floor(lz));
    const dx = lx - x0, dz = lz - z0;
    const i = (z0 * stride) * N + (x0 * stride);
    const s = stride;
    const h00 = t[i], h10 = t[i + s], h01 = t[i + s * N], h11 = t[i + s * N + s];
    const v = triSplit(h00, h10, h01, h11, dx, dz);
    return v * this.zScale + this.zBase;
  }

  stand(x, z) {
    const h = this.height(x, z);
    if (h === null) return null;
    const r = this.heightRender(x, z);
    return r === null ? h : Math.max(h, r);
  }
}

// the drawn L0 mesh's post stride from renderconfig (rings[0].patch_verts)
const renderStride = (cfg) => 256 / ((cfg.rings[0].patch_verts || 129) - 1);

module.exports = { HoleFallback, renderStride };
